import { useMemo, useState } from 'react'
import { useLinkedCommonDrawerState } from '../common/context/CommonDrawerContexts'
import { globalDispatch } from '../common/utils/Sparky'
import { LoadingPlaceholder } from '@sparky/framework'
import Identities from '../iam/components/identities/Identities'
import IDP from '../iam/components/idp/IDP'
import Roles from '../iam/components/roles/Roles'
import Header from './Header'
import Tabs from './Tabs'
import { MANAGE_USER_AND_ROLE } from '../common/constants/LabelConstants'
import { VIEW_ADD_CUSTOM_ROLE, VIEW_EDIT_CUSTOM_ROLE, VIEW_IAM_IDENTITY_FEDERATIONS, VIEW_MANAGE_IAM } from '../common/constants/GlobalConstants'
import CustomRoleForm from '../iam/components/roles/CustomRoleForm'
import IAMTabPanelWrapper from './IAMWrap'
import type { VFC } from 'react'
import type { Location } from 'history'
import type { NavItem } from '@sparky/framework'
import type { CommonModuleProps } from '../common/types'

type IAMTabsProps = CommonModuleProps & {
  navItem: NavItem,
  location: Location,
}

const RolesTab = (props: any) => {
  const cmPath = props.commonModule?.path
  const { navItem, ...rest } = props
  const View = useMemo(() => {
    const child = cmPath !== navItem.cmPath && navItem.children?.find((child: any) => child.cmPath === cmPath)
    if (child) {
      return child.component
    }
    return Roles
  }, [cmPath, navItem])

  return <View {...rest} />
}

const tabs = [
  {
    key: 'access',
    path: '/settings/iam/access',
    cmPath: VIEW_MANAGE_IAM,
    title: 'Access Management',
    showSideBar: true,
    lazy: false,
    component: Identities,
  },
  {
    key: 'roles',
    path: '/settings/iam/roles',
    cmPath: 'VIEW_IAM_ROLES',
    title: 'Roles',
    showSideBar: true,
    lazy: false,
    component: RolesTab,
    children: [
      {
        key: 'iam_add_role',
        path: '/settings/iam/roles/add',
        cmPath: VIEW_ADD_CUSTOM_ROLE,
        component: CustomRoleForm,
        showSideBar: false,
      },
      {
        key: 'iam_edit_role',
        path: '/settings/iam/roles/edit',
        cmPath: VIEW_EDIT_CUSTOM_ROLE,
        component: CustomRoleForm,
        showSideBar: false,
      },
    ]
  },
  {
    key: 'identities',
    path: '/settings/iam/identities',
    cmPath: VIEW_IAM_IDENTITY_FEDERATIONS,
    title: 'Identity Federations',
    showSideBar: false,
    lazy: false,
    component: IDP,
  },
] as NavItem[]
const mspTabs = tabs.map(tab => ({
  ...tab,
  path: `/msp${tab.path}`,
  children: tab.children?.map(child => ({
    ...child,
    path: `/msp${child.path}`,
  }))
}))
type PathMap = Record<string, string | undefined>
const tabPathReducer = (acc: PathMap, tab: NavItem) => {
  if (tab.path) {
    acc[tab.path] = tab.cmPath
  }
  if (Array.isArray(tab.children)) {
    tab.children.reduce(tabPathReducer, acc)
  }
  return acc
}
const locPathToCmPathMapping = tabs.concat(mspTabs).reduce(tabPathReducer, {} as PathMap)

const IAMTabs: VFC<IAMTabsProps> = ({ navItem, auth, commonModule, location }) => {
  const {
    treeReducer,
    viewReducer,
    instanceReducer,
    appReducer,
    licenseReducer,
    iamReducer,
    deviceReducer,
    subscriptionReducer,
    dispatch,
    initializing,
  } = useLinkedCommonDrawerState()

  const [canListPolicies, setCanListPolicies] = useState(true)
  const [canListRoles, setCanListRoles] = useState(true)

  const header = useMemo(() => <Header title={MANAGE_USER_AND_ROLE} />, [])

  const props = useMemo(() => {
    const path = locPathToCmPathMapping[location.pathname] || VIEW_MANAGE_IAM
    return {
      ...appReducer,
      ...treeReducer,
      ...viewReducer,
      ...instanceReducer,
      dispatch,
      iamReducer,
      licenseReducer,
      deviceReducer,
      globalDispatch,
      commonModule: {
        ...commonModule,
        path,
        visible: true,
      },
      subscriptionReducer,
      canListPolicies,
      setCanListPolicies,
      canListRoles,
      setCanListRoles,
      inCommonServicesPage: true,
      auth
    }
  }, [appReducer, auth, canListPolicies, canListRoles, commonModule, deviceReducer, dispatch, iamReducer, instanceReducer, licenseReducer, location.pathname, subscriptionReducer, treeReducer, viewReducer])

  if (initializing) {
    return <LoadingPlaceholder />
  }
  const tabsNavItem = { ...navItem, children: location.pathname.startsWith('/msp') ? mspTabs : tabs } as NavItem
  return (
    <div className='cm-w-full cm-h-full cm-flex cm-flex-col'>
      <Tabs panelProps={props} header={header} location={location} navItem={tabsNavItem} TabPanelWrapper={IAMTabPanelWrapper} />
    </div>
  )
}

export default IAMTabs
